import React, { useState, useEffect, useMemo, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../../auth";
import { Avatar } from "../../../../ui/components/Avatar";
import { Badge } from "../../../../ui/components/Badge";
import { Button } from "../../../../ui/components/Button";
import { DefaultPageLayout } from "../../../../ui/layouts/DefaultPageLayout";
import {
  FeatherHelpCircle,
  FeatherFileText,
  FeatherArrowRight,
  FeatherDownload,
  FeatherLoader,
  FeatherChevronDown,
  FeatherChevronUp,
  FeatherUpload,
  FeatherCheckCircle,
  FeatherXCircle,
  FeatherAlertTriangle,
  FeatherArrowLeft,
} from "@subframe/core";
import {
  generateReport,
  getReports,
  downloadReport,
  getAvailableTags,
  GenerateReportParams,
  SavedReport,
  checkRegistersAvailability as checkRegistersAvailabilityAPI,
  RegisterStatus,
} from "../../data/api/trialBalanceApi";
import {
  uploadDatabookRule,
  getDatabookRules,
  downloadDatabookRule,
  DatabookRule,
} from "../../data/api/databookRulesApi";
import { ExcelViewerModal } from "../components/ExcelViewerModal";

export const ReportsPage: React.FC = () => {
  const { company } = useAuth();
  const navigate = useNavigate();

  // Form state
  const [fromDate, setFromDate] = useState<string>("");
  const [toDate, setToDate] = useState<string>("");
  const [groupBy, setGroupBy] = useState<
    "monthwise" | "quarterly" | "yearly" | "6months"
  >("monthwise");
  const [isGenerating, setIsGenerating] = useState<boolean>(false);
  const [savedReports, setSavedReports] = useState<SavedReport[]>([]);
  const [isLoadingReports, setIsLoadingReports] = useState<boolean>(false);
  const [error, setError] = useState<string>("");
  const [success, setSuccess] = useState<string>("");

  // Tag filter state
  const [availableTags, setAvailableTags] = useState<{
    [tagName: string]: string[];
  }>({});
  const [selectedTagFilters, setSelectedTagFilters] = useState<{
    [tagName: string]: string[];
  }>({});
  const [isLoadingTags, setIsLoadingTags] = useState<boolean>(false);
  const [tagFiltersExpanded, setTagFiltersExpanded] = useState<boolean>(false);

  // Databook Rules state
  const [databookRules, setDatabookRules] = useState<DatabookRule[]>([]);
  const [isLoadingRules, setIsLoadingRules] = useState<boolean>(false);
  const [isUploadingRule, setIsUploadingRule] = useState<boolean>(false);
  const [uploadRuleError, setUploadRuleError] = useState<string>("");
  const [uploadRuleSuccess, setUploadRuleSuccess] = useState<string>("");
  const ruleFileInputRef = useRef<HTMLInputElement>(null);

  // View Report Modal state
  const [viewReportModal, setViewReportModal] = useState<{
    isOpen: boolean;
    reportId: number | null;
    reportName: string;
    fromDate?: string;
    toDate?: string;
  }>({
    isOpen: false,
    reportId: null,
    reportName: "",
  });

  // Register Check State
  const [registerCheckData, setRegisterCheckData] = useState<{
    registers: RegisterStatus[];
    overall_summary: {
      total_leads_with_registers: number;
      leads_with_all_months: number;
      leads_with_some_months: number;
      leads_with_no_months: number;
    };
    month_wise_summary: {
      available_months: Array<{ period_key: string; display: string }>;
      missing_months: Array<{ period_key: string; display: string }>;
      total_available: number;
      total_missing: number;
      total_periods: number;
    };
  } | null>(null);
  const [isCheckingRegisters, setIsCheckingRegisters] =
    useState<boolean>(false);
  const [expandedRegisters, setExpandedRegisters] = useState<Set<number>>(
    new Set(),
  );

  // Helper function to convert month (YYYY-MM) to first day of month (YYYY-MM-DD)
  const getFirstDayOfMonth = (monthStr: string): string => {
    return `${monthStr}-01`;
  };

  // Helper function to convert month (YYYY-MM) to last day of month (YYYY-MM-DD)
  const getLastDayOfMonth = (monthStr: string): string => {
    const [year, month] = monthStr.split("-").map(Number);
    // Get last day of month: day 0 of next month
    const lastDay = new Date(year, month, 0).getDate();
    return `${monthStr}-${String(lastDay).padStart(2, "0")}`;
  };

  // Build a list of month options for easy selection
  const buildMonthOptions = (
    monthsBack: number,
  ): Array<{ value: string; label: string }> => {
    const options: Array<{ value: string; label: string }> = [];
    const now = new Date();
    for (let i = 0; i <= monthsBack; i += 1) {
      const date = new Date(now.getFullYear(), now.getMonth() - i, 1);
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, "0");
      options.push({
        value: `${year}-${month}`,
        label: date.toLocaleDateString("en-US", {
          year: "numeric",
          month: "short",
        }),
      });
    }
    return options;
  };

  const monthOptions = useMemo(() => buildMonthOptions(120), []);

  // Generate client ID from company name
  const generateClientId = (companyName?: string): string => {
    if (!companyName) return "CLIENT-2024-001";
    const initials = companyName
      .split(" ")
      .map((word) => word.charAt(0))
      .join("")
      .toUpperCase();
    return `${initials}-2024-001`;
  };

  // Load saved reports and available tags on mount
  useEffect(() => {
    loadSavedReports();
    loadAvailableTags();
    loadDatabookRules();
  }, []);

  // Check register availability
  const checkRegistersAvailability = async (): Promise<void> => {
    if (!fromDate || !toDate) {
      setRegisterCheckData(null);
      return;
    }

    setIsCheckingRegisters(true);
    try {
      // Convert YYYY-MM format to YYYY-MM-DD format
      const fromDateFormatted = getFirstDayOfMonth(fromDate);
      const toDateFormatted = getLastDayOfMonth(toDate);

      const response = await checkRegistersAvailabilityAPI({
        from_date: fromDateFormatted,
        to_date: toDateFormatted,
        group_by: groupBy,
      });
      setRegisterCheckData(response.data);
    } catch (err: any) {
      console.error("Failed to check registers:", err);
      setError(`Failed to check registers: ${err.message || "Unknown error"}`);
      setRegisterCheckData(null);
    } finally {
      setIsCheckingRegisters(false);
    }
  };

  // Auto-check registers when dates or groupBy changes
  useEffect(() => {
    if (fromDate && toDate) {
      const timer = setTimeout(() => {
        checkRegistersAvailability();
      }, 500);
      return () => clearTimeout(timer);
    } else {
      setRegisterCheckData(null);
    }
  }, [fromDate, toDate, groupBy]);

  // Load saved reports
  const loadSavedReports = async (): Promise<void> => {
    setIsLoadingReports(true);
    setError("");
    try {
      const response = await getReports();

      setSavedReports(response.data.reports || []);
    } catch (err: any) {
      setError(`Failed to load reports: ${err.message || "Unknown error"}`);
    } finally {
      setIsLoadingReports(false);
    }
  };

  // Load available tags
  const loadAvailableTags = async (): Promise<void> => {
    setIsLoadingTags(true);
    try {
      const response = await getAvailableTags();
      console.log("Available tags loaded:", response.data);
      setAvailableTags(response.data || {});

      // Initialize selected filters with empty arrays (Select All = no filter)
      const initialFilters: { [tagName: string]: string[] } = {};
      Object.keys(response.data || {}).forEach((tagName) => {
        initialFilters[tagName] = [];
      });
      setSelectedTagFilters(initialFilters);
    } catch (err: any) {
      console.error("Failed to load available tags:", err);
      // Don't show error to user, just log it
    } finally {
      setIsLoadingTags(false);
    }
  };

  // Handle tag filter change
  const handleTagFilterChange = (
    tagName: string,
    tagValue: string,
    checked: boolean,
  ): void => {
    setSelectedTagFilters((prev: { [tagName: string]: string[] }) => {
      const current = prev[tagName] || [];
      let updated: string[];

      if (checked) {
        updated = [...current, tagValue];
      } else {
        updated = current.filter((v: string) => v !== tagValue);
      }

      return {
        ...prev,
        [tagName]: updated,
      };
    });
  };

  // Handle Select All for a tag
  const handleSelectAllTags = (tagName: string, checked: boolean): void => {
    setSelectedTagFilters((prev: { [tagName: string]: string[] }) => {
      if (checked) {
        // Select All = empty array (no filter)
        return {
          ...prev,
          [tagName]: [],
        };
      } else {
        // Deselect All = select all values (same as Select All)
        return {
          ...prev,
          [tagName]: [],
        };
      }
    });
  };

  // Check if Select All is active for a tag
  const isSelectAllActive = (tagName: string): boolean => {
    const selected = selectedTagFilters[tagName] || [];
    return selected.length === 0;
  };

  // Check if a specific tag value is selected
  const isTagValueSelected = (tagName: string, tagValue: string): boolean => {
    const selected = selectedTagFilters[tagName] || [];
    return selected.includes(tagValue);
  };

  // Handle raise query button
  const handleRaiseQuery = (): void => {
    navigate("/cfo/dashboard/queries");
  };

  // Handle navigate to income statement
  const handleNavigateToIncomeStatement = (): void => {
    navigate("/cfo/tb/reports/is");
  };

  // Handle generate report
  const handleGenerateReport = async (): Promise<void> => {
    setError("");
    setSuccess("");

    if (!fromDate || !toDate) {
      setError("Please select both From Month and To Month.");
      return;
    }

    if (new Date(fromDate) > new Date(toDate)) {
      setError("From Month must be before or equal to To Month.");
      return;
    }

    setIsGenerating(true);
    try {
      // Convert YYYY-MM format to YYYY-MM-DD format
      const fromDateFormatted = getFirstDayOfMonth(fromDate);
      const toDateFormatted = getLastDayOfMonth(toDate);

      const params: GenerateReportParams = {
        from_date: fromDateFormatted,
        to_date: toDateFormatted,
        group_by: groupBy,
        save_report: true,
        tag_filters: selectedTagFilters,
      };

      const response = await generateReport(params);
      console.log("Report generated:", response);
      setSuccess(`Report generated successfully: ${response.data.report_name}`);

      // Download the report automatically
      if (response.data.download_url) {
        window.open(response.data.download_url, "_blank");
      }

      // Reload saved reports after a short delay to ensure DB is updated
      setTimeout(() => {
        loadSavedReports();
      }, 500);
    } catch (err: any) {
      setError(err.message || "Failed to generate report. Please try again.");
    } finally {
      setIsGenerating(false);
    }
  };

  // Handle download saved report
  const handleDownloadReport = (report: SavedReport): void => {
    downloadReport(report.id, report.file_name);
  };

  // Handle view saved report
  const handleViewReport = (report: SavedReport): void => {
    setViewReportModal({
      isOpen: true,
      reportId: report.id,
      reportName: report.report_name,
      fromDate: report.from_date,
      toDate: report.to_date,
    });
  };

  // Format date for display
  const formatDate = (dateString: string): string => {
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  // Format date with time for display
  const formatDateTime = (dateString: string): string => {
    const date = new Date(dateString);
    const dateStr = date.toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
    const timeStr = date.toLocaleTimeString("en-US", {
      hour: "numeric",
      minute: "2-digit",
      hour12: true,
    });
    return `${dateStr} at ${timeStr}`;
  };

  // Load databook rules
  const loadDatabookRules = async (): Promise<void> => {
    setIsLoadingRules(true);
    setUploadRuleError("");
    try {
      const response = await getDatabookRules();
      setDatabookRules(response.data || []);
    } catch (err: any) {
      console.error("Failed to load databook rules:", err);
      setUploadRuleError(
        `Failed to load rules: ${err.message || "Unknown error"}`,
      );
    } finally {
      setIsLoadingRules(false);
    }
  };

  // Handle upload rule button click
  const handleUploadRuleClick = (): void => {
    ruleFileInputRef.current?.click();
  };

  // Handle rule file selection
  const handleRuleFileSelect = async (
    event: React.ChangeEvent<HTMLInputElement>,
  ): Promise<void> => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Reset messages
    setUploadRuleError("");
    setUploadRuleSuccess("");

    // Validate file type
    const extension = file.name.split(".").pop()?.toLowerCase();
    if (!extension || !["xlsx", "xls"].includes(extension)) {
      setUploadRuleError(
        "Invalid file type. Only .xlsx and .xls files are allowed.",
      );
      return;
    }

    // Validate file size (10MB)
    const maxSize = 10 * 1024 * 1024;
    if (file.size > maxSize) {
      setUploadRuleError("File size exceeds maximum limit of 10MB.");
      return;
    }

    // Upload file
    setIsUploadingRule(true);
    try {
      const response = await uploadDatabookRule(file);
      setUploadRuleSuccess(response.message || "Rule uploaded successfully!");

      // Reload rules list
      setTimeout(() => {
        loadDatabookRules();
        setUploadRuleSuccess("");
      }, 2000);

      // Clear file input
      if (ruleFileInputRef.current) {
        ruleFileInputRef.current.value = "";
      }
    } catch (err: any) {
      setUploadRuleError(
        err.message || "Failed to upload rule. Please try again.",
      );
    } finally {
      setIsUploadingRule(false);
    }
  };

  // Handle download rule
  const handleDownloadRule = (ruleId: number, fileName: string): void => {
    downloadDatabookRule(ruleId, fileName);
  };

  // Check if any register has missing data
  const hasAnyMissingRegisters =
    registerCheckData?.registers.some((r) => r.summary.without_registers > 0) ??
    false;

  // Toggle register expansion
  const toggleRegisterExpansion = (registerId: number): void => {
    const newExpanded = new Set(expandedRegisters);
    if (newExpanded.has(registerId)) {
      newExpanded.delete(registerId);
    } else {
      newExpanded.add(registerId);
    }
    setExpandedRegisters(newExpanded);
  };

  return (
    <DefaultPageLayout>
      <div className="flex h-full w-full flex-col items-start">
        {/* Common Header Section */}
        <div className="flex w-full flex-col items-start gap-4 border-b border-solid border-neutral-border bg-default-background px-8 py-6">
          <div className="flex w-full flex-wrap items-start justify-between">
            <div className="flex items-center gap-4">
              <Avatar
                size="large"
                image="https://images.unsplash.com/photo-1560179707-f14e90ef3623?w=800&auto=format&fit=crop&q=60&ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxzZWFyY2h8OHx8Y29tcGFueXxlbnwwfHwwfHx8MA%3D%3D"
              />
              <div className="flex flex-col items-start gap-1">
                <div className="flex items-center gap-2">
                  <span className="text-heading-2 font-heading-2 text-default-font">
                    {company?.companyName || "Acme Corporation"}
                  </span>
                  <Badge variant="warning">High Risk</Badge>
                </div>
                <span className="text-body font-body text-subtext-color">
                  Client ID: {generateClientId(company?.companyName)}
                </span>
              </div>
            </div>
            <Button
              variant="neutral-tertiary"
              icon={<FeatherHelpCircle />}
              onClick={handleRaiseQuery}
            >
              Raise Query
            </Button>
          </div>
        </div>

        {/* Main Content */}
        <div className="flex w-full grow shrink-0 basis-0 flex-col items-start bg-default-background overflow-auto">
          <div className="flex w-full h-full flex-col items-start p-8 gap-6">
            {/* Go Back Button */}
            <div className="flex w-full items-start">
              <Button
                variant="neutral-tertiary"
                icon={<FeatherArrowLeft />}
                onClick={() => navigate("/cfo/dashboard")}
              >
                Go Back to Dashboard
              </Button>
            </div>

            <div className="text-heading-1 font-heading-1 text-default-font">
              Reports
            </div>

            <div className="flex w-full max-w-6xl flex-col items-start gap-8">
              {/* Trial Balance Report Generator */}
              <div className="flex w-full flex-col items-start gap-4">
                <span className="text-heading-3 font-heading-3 text-default-font">
                  Create Databook
                </span>

                <div className="flex w-full flex-col gap-4 p-6 rounded-md border border-solid border-neutral-border bg-default-background">
                  {/* Month Selection */}
                  <div className="flex flex-col gap-2">
                    <label className="text-body font-body text-default-font">
                      Month Selection
                    </label>
                    <div className="flex items-center gap-4">
                      <div className="flex flex-col gap-1">
                        <label className="text-caption font-caption text-subtext-color">
                          From Month
                        </label>
                        <select
                          value={fromDate}
                          onChange={(e) => setFromDate(e.target.value)}
                          className="px-3 py-2 border border-solid border-neutral-border rounded-md text-body font-body"
                        >
                          <option value="">Select month</option>
                          {monthOptions.map((option) => (
                            <option
                              key={`from-${option.value}`}
                              value={option.value}
                            >
                              {option.label}
                            </option>
                          ))}
                        </select>
                      </div>
                      <div className="flex flex-col gap-1">
                        <label className="text-caption font-caption text-subtext-color">
                          To Month
                        </label>
                        <select
                          value={toDate}
                          onChange={(e) => setToDate(e.target.value)}
                          className="px-3 py-2 border border-solid border-neutral-border rounded-md text-body font-body"
                        >
                          <option value="">Select month</option>
                          {monthOptions.map((option) => (
                            <option
                              key={`to-${option.value}`}
                              value={option.value}
                            >
                              {option.label}
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>
                  </div>

                  {/* Register Status - Hidden for now */}
                  {false && fromDate && toDate && (
                    <div className="flex flex-col gap-2">
                      <div className="flex items-center justify-between">
                        <label className="text-body font-body text-default-font">
                          Register Status
                        </label>
                        <Button
                          variant="neutral-tertiary"
                          size="small"
                          onClick={checkRegistersAvailability}
                          disabled={isCheckingRegisters}
                          icon={
                            isCheckingRegisters ? (
                              <FeatherLoader className="animate-spin" />
                            ) : undefined
                          }
                        >
                          Refresh
                        </Button>
                      </div>

                      <div className="flex flex-col gap-4 p-4 rounded-md border border-neutral-border bg-neutral-50">
                        {isCheckingRegisters ? (
                          <div className="flex items-center gap-2 text-body font-body text-subtext-color">
                            <FeatherLoader className="animate-spin" />
                            <span>Checking register availability...</span>
                          </div>
                        ) : registerCheckData ? (
                          <>
                            {/* Month-wise Summary */}
                            {registerCheckData?.month_wise_summary && (
                              <div className="flex flex-col gap-3 p-4 rounded-md bg-white border border-neutral-border">
                                <span className="text-body-bold font-body-bold text-default-font">
                                  Month-wise Register Availability
                                </span>

                                {/* Available Months */}
                                {(registerCheckData?.month_wise_summary
                                  ?.available_months?.length ?? 0) > 0 && (
                                  <div className="flex flex-col gap-2">
                                    <div className="flex items-center gap-2">
                                      <FeatherCheckCircle className="text-green-600 w-4 h-4" />
                                      <span className="text-body font-body text-default-font">
                                        Registers available for:
                                      </span>
                                    </div>
                                    <div className="flex flex-wrap gap-2 ml-6">
                                      {registerCheckData?.month_wise_summary?.available_months?.map(
                                        (month) => (
                                          <span
                                            key={month.period_key}
                                            className="px-2 py-1 rounded-md bg-green-100 text-green-800 text-caption font-caption border border-green-200"
                                          >
                                            {month.display}
                                          </span>
                                        ),
                                      )}
                                    </div>
                                  </div>
                                )}

                                {/* Missing Months */}
                                {(registerCheckData?.month_wise_summary
                                  ?.missing_months?.length ?? 0) > 0 && (
                                  <div className="flex flex-col gap-2">
                                    <div className="flex items-center gap-2">
                                      <FeatherXCircle className="text-red-600 w-4 h-4" />
                                      <span className="text-body font-body text-default-font">
                                        Missing registers for:
                                      </span>
                                    </div>
                                    <div className="flex flex-wrap gap-2 ml-6">
                                      {registerCheckData?.month_wise_summary?.missing_months?.map(
                                        (month) => (
                                          <span
                                            key={month.period_key}
                                            className="px-2 py-1 rounded-md bg-red-100 text-red-800 text-caption font-caption border border-red-200"
                                          >
                                            {month.display}
                                          </span>
                                        ),
                                      )}
                                    </div>
                                  </div>
                                )}

                                {/* Summary Count */}
                                <div className="pt-2 border-t border-neutral-border">
                                  <span className="text-body font-body text-default-font">
                                    Summary:{" "}
                                    {
                                      registerCheckData?.month_wise_summary
                                        ?.total_available
                                    }{" "}
                                    of{" "}
                                    {
                                      registerCheckData?.month_wise_summary
                                        ?.total_periods
                                    }{" "}
                                    months have registers available
                                  </span>
                                </div>
                              </div>
                            )}

                            {/* Overall Summary */}
                            <div className="flex flex-col gap-2 p-3 rounded-md bg-white border border-neutral-border">
                              <span className="text-body-bold font-body-bold text-default-font">
                                Register Upload Summary (by Lead)
                              </span>
                              <div className="flex flex-col gap-1">
                                <span className="text-body font-body text-default-font">
                                  Total leads with registers:{" "}
                                  {
                                    registerCheckData?.overall_summary
                                      ?.total_leads_with_registers
                                  }
                                </span>
                                <div className="flex flex-wrap gap-4 mt-2">
                                  <div className="flex items-center gap-2">
                                    <FeatherCheckCircle className="text-green-600 w-4 h-4" />
                                    <span className="text-caption font-caption text-default-font">
                                      All months:{" "}
                                      {
                                        registerCheckData?.overall_summary
                                          ?.leads_with_all_months
                                      }
                                    </span>
                                  </div>
                                  <div className="flex items-center gap-2">
                                    <FeatherAlertTriangle className="text-amber-600 w-4 h-4" />
                                    <span className="text-caption font-caption text-default-font">
                                      Some months:{" "}
                                      {
                                        registerCheckData?.overall_summary
                                          ?.leads_with_some_months
                                      }
                                    </span>
                                  </div>
                                  <div className="flex items-center gap-2">
                                    <FeatherXCircle className="text-red-600 w-4 h-4" />
                                    <span className="text-caption font-caption text-default-font">
                                      No months:{" "}
                                      {
                                        registerCheckData?.overall_summary
                                          ?.leads_with_no_months
                                      }
                                    </span>
                                  </div>
                                </div>
                              </div>
                            </div>

                            {/* Register Cards */}
                            <div className="flex flex-col gap-3">
                              {registerCheckData?.registers?.map((register) => {
                                const hasData =
                                  register.summary.with_registers > 0;
                                const allMissing =
                                  register.summary.with_registers === 0;
                                const isExpanded = expandedRegisters.has(
                                  register.register_id,
                                );

                                return (
                                  <div
                                    key={register.register_id}
                                    className={`
                                      flex flex-col gap-3 p-4 rounded-md border
                                      ${
                                        allMissing
                                          ? "bg-red-50 border-red-200"
                                          : hasData &&
                                              register.summary
                                                .without_registers > 0
                                            ? "bg-amber-50 border-amber-200"
                                            : "bg-green-50 border-green-200"
                                      }
                                    `}
                                  >
                                    {/* Header */}
                                    <div className="flex items-center justify-between">
                                      <div className="flex items-center gap-2">
                                        {allMissing ? (
                                          <FeatherXCircle className="text-red-600 w-5 h-5" />
                                        ) : register.summary.without_registers >
                                          0 ? (
                                          <FeatherAlertTriangle className="text-amber-600 w-5 h-5" />
                                        ) : (
                                          <FeatherCheckCircle className="text-green-600 w-5 h-5" />
                                        )}
                                        <span className="text-body-bold font-body-bold text-default-font">
                                          {register.register_name}
                                        </span>
                                      </div>
                                      <Button
                                        variant="neutral-tertiary"
                                        size="small"
                                        onClick={() =>
                                          toggleRegisterExpansion(
                                            register.register_id,
                                          )
                                        }
                                        icon={
                                          isExpanded ? (
                                            <FeatherChevronUp />
                                          ) : (
                                            <FeatherChevronDown />
                                          )
                                        }
                                      >
                                        {isExpanded ? "Collapse" : "Expand"}
                                      </Button>
                                    </div>

                                    {/* Summary (Always Visible) */}
                                    <div className="flex flex-col gap-1">
                                      {allMissing ? (
                                        <span className="text-body font-body text-red-700">
                                          ✗ No data uploaded
                                        </span>
                                      ) : (
                                        <>
                                          <span className="text-body font-body text-default-font">
                                            {register.summary.with_registers} of{" "}
                                            {register.summary.total_periods}{" "}
                                            months uploaded
                                          </span>
                                          <div className="flex flex-wrap gap-1 items-center">
                                            <span className="text-caption font-caption text-subtext-color">
                                              Uploaded:
                                            </span>
                                            {register.summary.uploaded_months
                                              .slice(0, 5)
                                              .map((month, idx) => (
                                                <span
                                                  key={idx}
                                                  className="text-caption font-caption text-green-700"
                                                >
                                                  {month}
                                                </span>
                                              ))}
                                            {register.summary.uploaded_months
                                              .length > 5 && (
                                              <span className="text-caption font-caption text-subtext-color">
                                                +
                                                {register.summary
                                                  .uploaded_months.length -
                                                  5}{" "}
                                                more
                                              </span>
                                            )}
                                          </div>
                                          {register.summary.missing_months
                                            .length > 0 && (
                                            <div className="flex flex-wrap gap-1 items-center">
                                              <span className="text-caption font-caption text-subtext-color">
                                                Missing:
                                              </span>
                                              {register.summary.missing_months.map(
                                                (month, idx) => (
                                                  <span
                                                    key={idx}
                                                    className="text-caption font-caption text-red-700"
                                                  >
                                                    {month}
                                                  </span>
                                                ),
                                              )}
                                            </div>
                                          )}
                                        </>
                                      )}
                                    </div>

                                    {/* Expanded Details */}
                                    {isExpanded && (
                                      <div className="flex flex-col gap-3 pt-3 border-t border-neutral-border">
                                        {/* Period Grid */}
                                        <div className="flex flex-col gap-2">
                                          <span className="text-caption font-caption text-subtext-color">
                                            Period Status:
                                          </span>
                                          <div className="flex flex-wrap gap-2">
                                            {register.periods.map(
                                              (period, idx) => (
                                                <div
                                                  key={idx}
                                                  className={`
                                                  flex items-center gap-1 px-2 py-1 rounded border
                                                  ${
                                                    period.has_register
                                                      ? "bg-green-100 border-green-300"
                                                      : "bg-red-100 border-red-300"
                                                  }
                                                `}
                                                >
                                                  {period.has_register ? (
                                                    <FeatherCheckCircle className="text-green-600 w-3 h-3" />
                                                  ) : (
                                                    <FeatherXCircle className="text-red-600 w-3 h-3" />
                                                  )}
                                                  <span
                                                    className={`
                                                  text-caption font-caption
                                                  ${period.has_register ? "text-green-700" : "text-red-700"}
                                                `}
                                                  >
                                                    {period.display}
                                                  </span>
                                                </div>
                                              ),
                                            )}
                                          </div>
                                        </div>

                                        {/* Action Button */}
                                        {register.summary.missing_months
                                          .length > 0 && (
                                          <Button
                                            variant="neutral-tertiary"
                                            size="small"
                                            onClick={() =>
                                              navigate("/cfo/registers/upload")
                                            }
                                          >
                                            Upload Missing Months →
                                          </Button>
                                        )}
                                      </div>
                                    )}
                                  </div>
                                );
                              })}
                            </div>
                          </>
                        ) : null}
                      </div>
                    </div>
                  )}

                  {/* Group By */}
                  <div className="flex flex-col gap-2">
                    <label className="text-body font-body text-default-font">
                      Group By
                    </label>
                    <div className="flex flex-wrap gap-4">
                      {(
                        ["monthwise", "quarterly", "yearly", "6months"] as const
                      ).map((option) => (
                        <label
                          key={option}
                          className="flex items-center gap-2 cursor-pointer"
                        >
                          <input
                            type="radio"
                            name="groupBy"
                            value={option}
                            checked={groupBy === option}
                            onChange={() => setGroupBy(option)}
                            className="cursor-pointer"
                          />
                          <span className="text-body font-body text-default-font capitalize">
                            {option === "6months" ? "6 Months" : option}
                          </span>
                        </label>
                      ))}
                    </div>
                  </div>

                  {/* Filter by Tags */}
                  {Object.keys(availableTags).length > 0 && (
                    <div className="flex flex-col gap-2">
                      <button
                        type="button"
                        onClick={() =>
                          setTagFiltersExpanded(!tagFiltersExpanded)
                        }
                        className="flex items-center justify-between text-body font-body text-default-font cursor-pointer hover:text-brand-600 transition-colors"
                      >
                        <span>Filter by Tags</span>
                        {tagFiltersExpanded ? (
                          <FeatherChevronUp />
                        ) : (
                          <FeatherChevronDown />
                        )}
                      </button>

                      {tagFiltersExpanded && (
                        <div className="flex flex-col gap-4 p-4 rounded-md border border-solid border-neutral-border bg-neutral-50">
                          {isLoadingTags ? (
                            <div className="flex items-center gap-2 text-body font-body text-subtext-color">
                              <FeatherLoader className="animate-spin" />
                              <span>Loading tags...</span>
                            </div>
                          ) : (
                            <div className="flex flex-wrap gap-6">
                              {Object.entries(availableTags).map(
                                ([tagName, tagValues]: [string, string[]]) => (
                                  <div
                                    key={tagName}
                                    className="flex flex-col gap-2 min-w-[200px]"
                                  >
                                    <div className="flex items-center gap-2">
                                      <label className="flex items-center gap-2 cursor-pointer">
                                        <input
                                          type="checkbox"
                                          checked={isSelectAllActive(tagName)}
                                          onChange={(
                                            e: React.ChangeEvent<HTMLInputElement>,
                                          ) =>
                                            handleSelectAllTags(
                                              tagName,
                                              e.target.checked,
                                            )
                                          }
                                          className="cursor-pointer"
                                        />
                                        <span className="text-body-bold font-body-bold text-default-font">
                                          {tagName} - Select All
                                        </span>
                                      </label>
                                    </div>
                                    <div className="flex flex-col gap-1 pl-6">
                                      {tagValues.map((tagValue: string) => (
                                        <label
                                          key={tagValue}
                                          className="flex items-center gap-2 cursor-pointer"
                                        >
                                          <input
                                            type="checkbox"
                                            checked={isTagValueSelected(
                                              tagName,
                                              tagValue,
                                            )}
                                            onChange={(
                                              e: React.ChangeEvent<HTMLInputElement>,
                                            ) =>
                                              handleTagFilterChange(
                                                tagName,
                                                tagValue,
                                                e.target.checked,
                                              )
                                            }
                                            className="cursor-pointer"
                                          />
                                          <span className="text-body font-body text-default-font">
                                            {tagValue}
                                          </span>
                                        </label>
                                      ))}
                                    </div>
                                  </div>
                                ),
                              )}
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  )}

                  {/* Error/Success Messages */}
                  {error && (
                    <div className="px-4 py-2 bg-red-50 border border-red-200 rounded-md text-body font-body text-red-700">
                      {error}
                    </div>
                  )}
                  {success && (
                    <div className="px-4 py-2 bg-green-50 border border-green-200 rounded-md text-body font-body text-green-700">
                      {success}
                    </div>
                  )}

                  {/* Generate Button */}
                  <Button
                    variant="brand-primary"
                    onClick={handleGenerateReport}
                    disabled={isGenerating || !fromDate || !toDate}
                    icon={
                      isGenerating ? (
                        <FeatherLoader className="animate-spin" />
                      ) : hasAnyMissingRegisters ? (
                        <FeatherAlertTriangle />
                      ) : (
                        <FeatherFileText />
                      )
                    }
                  >
                    {isGenerating
                      ? "Generating Report..."
                      : hasAnyMissingRegisters
                        ? `Generate Report (Some registers incomplete)`
                        : "Generate Report"}
                  </Button>
                </div>
              </div>

              {/* Saved Reports */}
              <div className="flex w-full flex-col items-start gap-4">
                <div className="flex w-full items-center justify-between">
                  <span className="text-heading-3 font-heading-3 text-default-font">
                    Saved Databook
                  </span>
                  <Button
                    variant="neutral-tertiary"
                    onClick={loadSavedReports}
                    disabled={isLoadingReports}
                    icon={
                      isLoadingReports ? (
                        <FeatherLoader className="animate-spin" />
                      ) : undefined
                    }
                  >
                    {isLoadingReports ? "Loading..." : "Refresh"}
                  </Button>
                </div>

                {isLoadingReports ? (
                  <div className="flex items-center gap-2 text-body font-body text-subtext-color">
                    <FeatherLoader className="animate-spin" />
                    <span>Loading reports...</span>
                  </div>
                ) : savedReports.length === 0 ? (
                  <div className="text-body font-body text-subtext-color">
                    No saved reports yet. Generate a report to see it here.
                  </div>
                ) : (
                  <div className="flex w-full flex-col gap-3">
                    {savedReports.map((report) => (
                      <div
                        key={report.id}
                        className="flex w-full items-center justify-between rounded-md border border-solid border-neutral-border bg-default-background p-4 hover:bg-neutral-50 transition-colors"
                      >
                        <div className="flex items-center gap-4">
                          <div className="flex items-center justify-center w-10 h-10 rounded-md bg-brand-50">
                            <FeatherFileText className="text-brand-600" />
                          </div>
                          <div className="flex flex-col items-start gap-1">
                            <span className="text-body-bold font-body-bold text-default-font">
                              {report.report_name}
                            </span>
                            <span className="text-caption font-caption text-subtext-color">
                              {formatDate(report.from_date)} to{" "}
                              {formatDate(report.to_date)} • {report.group_by}
                            </span>
                            {report.tag_filters &&
                              (() => {
                                try {
                                  const filters = JSON.parse(
                                    report.tag_filters,
                                  );
                                  const filterCount =
                                    Object.keys(filters).length;
                                  if (filterCount > 0) {
                                    return (
                                      <span className="text-caption font-caption text-brand-600">
                                        Filters applied: {filterCount} tag
                                        {filterCount !== 1 ? "s" : ""}
                                      </span>
                                    );
                                  }
                                } catch (e) {
                                  // Invalid JSON, ignore
                                }
                                return null;
                              })()}
                            <span className="text-caption font-caption text-subtext-color">
                              Created: {formatDateTime(report.created_at)}
                            </span>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <Button
                            variant="brand-primary"
                            onClick={() => handleViewReport(report)}
                          >
                            View Report
                          </Button>
                          <Button
                            variant="neutral-tertiary"
                            icon={<FeatherDownload />}
                            onClick={() => handleDownloadReport(report)}
                          >
                            Download
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Databook Rules Section */}
              <div className="hidden flex w-full flex-col items-start gap-4">
                <div className="flex w-full items-center justify-between">
                  <span className="text-heading-3 font-heading-3 text-default-font">
                    Databook Rules
                  </span>
                  <Button
                    variant="brand-primary"
                    icon={<FeatherUpload />}
                    onClick={handleUploadRuleClick}
                    disabled={isUploadingRule}
                  >
                    {isUploadingRule ? "Uploading..." : "Upload Rule"}
                  </Button>
                </div>

                {/* Hidden file input */}
                <input
                  ref={ruleFileInputRef}
                  type="file"
                  accept=".xlsx,.xls"
                  style={{ display: "none" }}
                  onChange={handleRuleFileSelect}
                />

                {/* Upload error/success messages */}
                {uploadRuleError && (
                  <div className="w-full px-4 py-2 bg-red-50 border border-red-200 rounded-md text-body font-body text-red-700">
                    {uploadRuleError}
                  </div>
                )}
                {uploadRuleSuccess && (
                  <div className="w-full px-4 py-2 bg-green-50 border border-green-200 rounded-md text-body font-body text-green-700">
                    {uploadRuleSuccess}
                  </div>
                )}

                {/* List of uploaded rules */}
                {isLoadingRules ? (
                  <div className="flex items-center gap-2 text-body font-body text-subtext-color">
                    <FeatherLoader className="animate-spin" />
                    <span>Loading rules...</span>
                  </div>
                ) : databookRules.length === 0 ? (
                  <div className="text-body font-body text-subtext-color">
                    No rules uploaded yet. Click "Upload Rule" to add one.
                  </div>
                ) : (
                  <div className="flex w-full flex-col gap-3">
                    {databookRules.map((rule) => (
                      <div
                        key={rule.id}
                        className="flex w-full items-center justify-between rounded-md border border-solid border-neutral-border bg-default-background p-4 hover:bg-neutral-50 transition-colors"
                      >
                        <div className="flex items-center gap-4">
                          <div className="flex items-center justify-center w-10 h-10 rounded-md bg-brand-50">
                            <FeatherFileText className="text-brand-600" />
                          </div>
                          <div className="flex flex-col items-start gap-1">
                            <span className="text-body-bold font-body-bold text-default-font">
                              {rule.file_name}
                            </span>
                            <span className="text-caption font-caption text-subtext-color">
                              Uploaded: {formatDate(rule.created_at)}
                            </span>
                          </div>
                        </div>
                        <Button
                          variant="neutral-tertiary"
                          icon={<FeatherDownload />}
                          onClick={() =>
                            handleDownloadRule(rule.id, rule.file_name)
                          }
                        >
                          Download
                        </Button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* View Report Modal */}
      {viewReportModal.reportId && (
        <ExcelViewerModal
          isOpen={viewReportModal.isOpen}
          onClose={() =>
            setViewReportModal({
              isOpen: false,
              reportId: null,
              reportName: "",
            })
          }
          reportId={viewReportModal.reportId}
          reportName={viewReportModal.reportName}
          fromDate={viewReportModal.fromDate}
          toDate={viewReportModal.toDate}
        />
      )}
    </DefaultPageLayout>
  );
};
